import { FC } from "react";
import type { CreateWheelRaffleResponse } from "entities/wheel-raffle";
import { formStyles } from "shared/ui";
import { ArrowRightIcon } from "shared/ui/icons";
import { useCreateRaffle } from "../model/useCreateRaffle";
import { AmountFields } from "./AmountFields";
import { FormSection } from "./FormSection";
import { RafflePreviewPanel } from "./RafflePreviewPanel";
import { RulesFields } from "./RulesFields";
import { VisibilityFields } from "./VisibilityFields";

import styles from "./CreateWheelRaffleForm.module.css";

interface Props {
  onCreated?: (raffle: CreateWheelRaffleResponse) => void;
}

export const CreateWheelRaffleForm: FC<Props> = ({ onCreated }) => {
  const form = useCreateRaffle(onCreated);
  const { meta, isSubmitting } = form;

  if (form.isMetaLoading) return <p className={formStyles.hint}>Loading...</p>;
  if (form.isMetaError || !meta) {
    return <p className={formStyles.error}>Failed to load promo settings.</p>;
  }

  return (
    <form
      className={styles.layout}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        form.submit();
      }}
    >
      <div className={styles.sections}>
        <FormSection step={1} title="Prize">
          <AmountFields form={form} />
        </FormSection>
        <FormSection step={2} title="Rules">
          <RulesFields
            form={form}
            symbols={meta.symbols}
            tiers={meta.participant_tiers}
          />
        </FormSection>
        <FormSection step={3} title="Access">
          <VisibilityFields form={form} />
        </FormSection>
      </div>
      <aside className={styles.aside}>
        <RafflePreviewPanel form={form} />
        {form.serverError && (
          <p className={formStyles.error}>{form.serverError}</p>
        )}
        <button
          type="submit"
          className={`${formStyles.submitButton} ${styles.submit}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create promo"}
          <ArrowRightIcon className={styles.arrow} aria-hidden="true" />
        </button>
      </aside>
    </form>
  );
};
